/**
 * TTU Semester Cost Calculator
 *
 * A React component that calculates semester costs for Texas Tech University students.
 * Features include:
 * - Credit hour cost calculation
 * - Scholarship application (10-70%)
 * - TSI course cost calculation
 * - Tax calculation
 * - Excel export functionality
 *
 * @component
 */

import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import ttuLogo from "../assets/TTUlogo.png"; // Updated import with correct filename
import semesterCalculatorHeader from "../assets/semester_calculator.jpg";
import config from "../config"; // Import local config

// Texas Tech colors
const TTU_COLORS = {
  red: "#CC0000",
  black: "#000000",
  darkRed: "#990000",
  lightGray: "#f8f9fa",
  white: "#ffffff",
};

const Calculator = () => {
  const [credits, setCredits] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [hasScholarship, setHasScholarship] = useState(false);
  const [scholarshipPercentage, setScholarshipPercentage] = useState("");
  const [hasTSI, setHasTSI] = useState(false);
  const [tsiCourses, setTSICourses] = useState("");

  // Replace useEffect that fetches config
  useEffect(() => {
    console.log("Using local config:", config);
  }, []);

  // Add this useEffect to recalc costs when scholarship settings change
  useEffect(() => {
    if (credits !== "") {
      calculateCost(credits, scholarshipPercentage);
    }
  }, [hasScholarship, scholarshipPercentage]);

  /**
   * Handles changes to the credit hours input
   * Validates and normalizes input values
   * @param {Event} e - Input change event
   */
  const handleCreditsChange = (e) => {
    let newCredits = e.target.value;
    console.log("Credits changed to:", newCredits);

    // Ensure credits don't exceed 19
    if (newCredits !== "" && Number(newCredits) > 19) {
      newCredits = "19";
    }
    // If scholarship is active, enforce a minimum of 12 credits
    if (hasScholarship && newCredits !== "" && Number(newCredits) < 12) {
      newCredits = "12";
    }

    setCredits(newCredits);

    // Call calculateCost only if there is a valid value
    if (newCredits !== "") {
      calculateCost(newCredits);
    }
  };

  /**
   * Handles scholarship checkbox changes
   * Sets default scholarship percentage and validates credit requirements
   * @param {Event} e - Checkbox change event
   */
  const handleScholarshipChange = (e) => {
    const isChecked = e.target.checked;
    setHasScholarship(isChecked);

    if (!isChecked) {
      setScholarshipPercentage("");
      calculateCost(credits);
    } else {
      // Set default value to 10%
      setScholarshipPercentage("10");

      // If credits are empty, don't calculate
      if (credits === "") return;

      // If credits are less than 12, set to 12 and calculate
      if (Number(credits) < 12) {
        setCredits("12");
        calculateCost("12", "10");
      } else {
        calculateCost(credits, "10");
      }
    }
  };

  /**
   * Handles scholarship percentage changes
   * Triggers cost recalculation
   * @param {Event} e - Select change event
   */
  const handleScholarshipPercentageChange = (e) => {
    const value = e.target.value;
    setScholarshipPercentage(value);

    if (credits !== "") {
      calculateCost(credits, value);
    }
  };

  /**
   * Handles TSI checkbox changes
   * Sets default TSI course count
   * @param {Event} e - Checkbox change event
   */
  const handleTSIChange = (e) => {
    const isChecked = e.target.checked;
    setHasTSI(isChecked);
    if (!isChecked) {
      setTSICourses("");
      calculateCost(credits, scholarshipPercentage, "", false);
    } else {
      // Set default value to 1 course
      setTSICourses("1");
      if (credits !== "") {
        calculateCost(credits, scholarshipPercentage, "1", true);
      }
    }
  };

  /**
   * Handles TSI course count changes
   * @param {Event} e - Select change event
   */
  const handleTSICoursesChange = (e) => {
    const value = e.target.value;
    setTSICourses(value);
    calculateCost(credits, scholarshipPercentage, value);
  };

  /**
   * Resets all form inputs and results
   */
  const handleClean = () => {
    setCredits("");
    setHasScholarship(false);
    setScholarshipPercentage("");
    setHasTSI(false);
    setTSICourses("");
    setResult(null);
    setError(null);
  };

  /**
   * Recalculates costs with current values
   */
  const handleRefresh = () => {
    setError(null);
    // Recalculate with current values if they exist
    if (credits !== "") {
      calculateCost(credits);
    }
  };

  /**
   * Calculates semester costs based on input values
   * Includes tuition, fees, scholarships, and taxes
   * @param {string} creditsVal - Number of credit hours
   * @param {string} scholarshipVal - Scholarship percentage
   * @param {string} tsiVal - Number of TSI courses
   * @param {boolean} tsiActive - Whether TSI is active
   */
  const calculateCost = (
    creditsVal,
    scholarshipVal = scholarshipPercentage,
    tsiVal = tsiCourses,
    tsiActive = hasTSI
  ) => {
    if (creditsVal === "") return;

    setError(null);

    try {
      const credits = Number(creditsVal);
      const minCredits = hasScholarship ? 12 : 1; // updated minimum based on scholarship
      if (credits < minCredits || credits > 19) {
        setError(`Number of credits must be between ${minCredits} and 19`);
        return;
      }

      const scholarshipValue = hasScholarship
        ? Math.max(10, Math.min(70, Number(scholarshipVal) || 10))
        : 0;
      // Use tsiActive instead of hasTSI for reliable calculation
      const tsiValue = tsiActive
        ? Math.max(1, Math.min(2, Number(tsiVal) || 1))
        : 0;

      // Calculate TSI cost
      const tsiCost = tsiValue * config.tsi_course_cost;

      // Calculate base tuition for credits only
      const baseTuition = credits * config.credit_price;

      // Apply scholarship discount only to credit tuition
      let scholarshipAmount = 0;
      if (hasScholarship) {
        scholarshipAmount = baseTuition * (scholarshipValue / 100);
      }
      const discountedCreditTuition = baseTuition - scholarshipAmount;

      // Final tuition includes TSI cost in full
      let tuition = discountedCreditTuition + (tsiActive ? tsiCost : 0);

      // Calculate totals
      const subtotal = tuition + config.insurance + config.student_services;
      const eduTaxed = tuition * (1 + config.tax_edu);
      const servicesTaxed =
        (config.student_services + config.insurance) * (1 + config.tax_iva);
      const totalCost = eduTaxed + servicesTaxed;
      const defferedPayment = totalCost / config.deffered_payments;

      const result = {
        credits,
        tuition,
        insurance: config.insurance,
        student_services: config.student_services,
        tsi_cost: tsiActive ? tsiCost : 0,
        tsi_course_cost: config.tsi_course_cost,
        subtotal,
        tax_iva: config.tax_iva,
        tax_edu: config.tax_edu,
        total_cost: totalCost,
        has_scholarship: hasScholarship,
        scholarship_percentage: scholarshipValue,
        scholarship_amount: scholarshipAmount,
        has_tsi: tsiActive,
        tsi_courses: tsiValue,
        deffered_payment: defferedPayment,
      };

      setResult(result);
    } catch (error) {
      setError("Failed to calculate cost: " + error.message);
      setResult(null);
    }
  };

  /**
   * Generates and downloads an Excel file with calculation results
   * Includes detailed breakdown of all costs and fees
   */
  const handleDownload = () => {
    if (result) {
      try {
        // Create array of arrays for vertical data layout
        const excelData = [
          // Basic Information
          ["Semester Information"],
          ["Number of Credits", result.credits],
          ["Credit Price", `$${config.credit_price.toFixed(2)}`],
          [""], // Empty row for separation

          // Course Costs
          ["Course Costs"],
          ["Base Tuition", `$${result.tuition.toFixed(2)}`],

          // TSI Information (if applicable)
          ...(result.tsi_cost > 0
            ? [
                [""], // Empty row
                ["TSI Information"],
                ["Number of TSI Courses", result.tsi_courses],
                ["TSI Course Price", `$${config.tsi_course_cost.toFixed(2)}`],
                ["Total TSI Cost", `$${result.tsi_cost.toFixed(2)}`],
              ]
            : []),

          [""], // Empty row for separation

          // Additional Fees
          ["Additional Fees"],
          ["Insurance", `$${result.insurance.toFixed(2)}`],
          ["Student Services", `$${result.student_services.toFixed(2)}`],
          [""], // Empty row for separation

          // Scholarship Information (if applicable)
          ...(hasScholarship
            ? [
                ["Scholarship Information"],
                ["Scholarship Percentage", `${result.scholarship_percentage}%`],
                [
                  "Scholarship Amount",
                  `-$${result.scholarship_amount.toFixed(2)}`,
                ],
                [""], // Empty row for separation
              ]
            : []),

          // Tax Information
          ["Tax Information"],
          ["Educational Tax Rate", `${(config.tax_edu * 100).toFixed(1)}%`],
          ["Services Tax Rate", `${(config.tax_iva * 100).toFixed(1)}%`],
          [""], // Empty row for separation

          // Cost Summary
          ["Cost Summary"],
          ["Subtotal (Before Tax)", `$${result.subtotal.toFixed(2)}`],
          ["Total Cost", `$${result.total_cost.toFixed(2)}`],
          [""], // Empty row for separation

          // Payment Information
          ["Payment Information"],
          [
            "Monthly Payment (4 months)",
            `$${(result.total_cost / 4).toFixed(2)}`,
          ],
          [
            "Note",
            "* Deferred payments of 3 or 6 months can be obtained with BAC or Promerica Banks",
          ],
        ];

        console.log("Creating Excel file with vertical data layout");

        // Create worksheet
        const ws = XLSX.utils.aoa_to_sheet(excelData);

        // Set column widths
        ws["!cols"] = [
          { wch: 25 }, // Column A - Labels
          { wch: 45 }, // Column B - Values
        ];

        // Create workbook and append worksheet
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Semester Costs");

        // Generate the file
        XLSX.writeFile(wb, "TTU_Semester_Costs.xlsx");
      } catch (error) {
        console.error("Error creating Excel file:", error);
        setError("Failed to create Excel file. Please try again.");
      }
    }
  };

  /**
   * Shows information about TSI courses
   */
  const showTSIAlert = () => {
    alert(
      "Texas Success Initiative (TSI) courses are preparatory courses required for students who need additional support in core subjects."
    );
  };

  return (
    <div
      style={{
        padding: "20px",
        maxWidth: "800px",
        margin: "0 auto",
        backgroundColor: TTU_COLORS.white,
        minHeight: "100vh",
      }}
    >
      {/* Header with Logo and Title */}
      <header style={{ margin: "-20px -20px 0 -20px" }}>
        <img src={semesterCalculatorHeader} alt="Semester Cost Calculator Header" style={{ width: "100%", height: "auto", display: "block", marginBottom: "16px" }} />
      </header>

      {/* Action Buttons */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          marginBottom: "20px",
        }}
      >
        <button
          onClick={handleClean}
          style={{
            padding: "8px 16px",
            backgroundColor: TTU_COLORS.black,
            color: TTU_COLORS.white,
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            transition: "background-color 0.2s",
            fontSize: "0.9em",
            fontWeight: "bold",
          }}
          onMouseOver={(e) => (e.target.style.backgroundColor = "#333")}
          onMouseOut={(e) =>
            (e.target.style.backgroundColor = TTU_COLORS.black)
          }
        >
          Clean All
        </button>
        <button
          onClick={handleRefresh}
          style={{
            padding: "8px 16px",
            backgroundColor: TTU_COLORS.red,
            color: TTU_COLORS.white,
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            transition: "background-color 0.2s",
            fontSize: "0.9em",
            fontWeight: "bold",
          }}
          onMouseOver={(e) =>
            (e.target.style.backgroundColor = TTU_COLORS.darkRed)
          }
          onMouseOut={(e) => (e.target.style.backgroundColor = TTU_COLORS.red)}
        >
          Refresh Data
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div
          style={{
            padding: "10px",
            backgroundColor: "#fee2e2",
            color: TTU_COLORS.red,
            borderRadius: "8px",
            marginBottom: "20px",
            border: `1px solid ${TTU_COLORS.red}`,
          }}
        >
          {error}
        </div>
      )}

      {/* Configuration Panel */}
      {/* 
      {config && (
        <div
          style={{
            marginBottom: "20px",
            padding: "20px",
            backgroundColor: TTU_COLORS.lightGray,
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            border: `1px solid ${TTU_COLORS.red}`,
          }}
        >
          <h3
            style={{
              color: TTU_COLORS.red,
              marginBottom: "15px",
              marginTop: 0,
              fontFamily: "'Roboto', sans-serif",
              borderBottom: `2px solid ${TTU_COLORS.red}`,
              paddingBottom: "8px",
            }}
          >
            Current Pricing Configuration
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "10px",
            }}
          >
            <p style={{ margin: "5px 0" }}>
              <strong>Credit Price:</strong> ${config.credit_price}
            </p>
            <p style={{ margin: "5px 0" }}>
              <strong>TSI Course Price:</strong> ${config.tsi_course_cost}
            </p>
            <p style={{ margin: "5px 0" }}>
              <strong>Insurance:</strong> ${config.insurance}
            </p>
            <p style={{ margin: "5px 0" }}>
              <strong>Student Services:</strong> ${config.student_services}
            </p>
            <p style={{ margin: "5px 0" }}>
              <strong>Educational Tax:</strong> {config.tax_edu}%
            </p>
            <p style={{ margin: "5px 0" }}>
              <strong>Services Tax:</strong> {config.tax_iva}%
            </p>
          </div>
        </div>
      )}
      */}

      {/* Input Panel */}
      <div
        style={{
          marginBottom: "20px",
          padding: "20px",
          backgroundColor: TTU_COLORS.white,
          borderRadius: "8px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          border: `1px solid ${TTU_COLORS.black}`,
        }}
      >
        <div style={{ marginBottom: "15px" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontWeight: "bold", minWidth: "120px" }}>
              Number of Credits:
            </span>
            <input
              type="number"
              step="1"
              min={hasScholarship ? "12" : "1"} // updated: dynamic min value based on scholarship
              max="19"
              value={credits}
              onChange={(e) => {
                let newCredits = e.target.value;
                // Automatically set to "12" if scholarship is active and the first digit is not "1"
                if (
                  hasScholarship &&
                  newCredits.length === 1 &&
                  Number(newCredits) > 1
                ) {
                  newCredits = "12";
                }
                // Enforce maximum limit
                if (newCredits !== "" && Number(newCredits) > 19) {
                  newCredits = "19";
                }
                setCredits(newCredits);
                if (newCredits !== "") {
                  calculateCost(newCredits);
                }
              }}
              onBlur={(e) => {
                let value = e.target.value;
                const minCredits = hasScholarship ? 12 : 1;
                if (value === "") {
                  value = minCredits.toString();
                } else {
                  value = Math.min(
                    19,
                    Math.max(minCredits, Number(value))
                  ).toString();
                }
                setCredits(value);
                calculateCost(value);
              }}
              style={{
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ced4da",
                width: "100px",
              }}
            />
            <span
              style={{ fontSize: "0.9em", color: "#666", marginLeft: "10px" }}
            >
              (max: 19 credits
              {hasScholarship
                ? ", min: 12 credits required for scholarship"
                : ""}
              )
            </span>
          </label>
        </div>

        <div style={{ marginBottom: "15px" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <input
              type="checkbox"
              checked={hasScholarship}
              onChange={handleScholarshipChange}
              id="scholarship-checkbox"
              style={{
                cursor: "pointer",
                width: "16px",
                height: "16px",
              }}
            />
            <span
              style={{
                fontWeight: "bold",
                marginLeft: "10px",
                userSelect: "none", // Prevents text selection
              }}
            >
              Has Scholarship
            </span>
          </div>
        </div>

        {hasScholarship && (
          <div style={{ marginBottom: "15px" }}>
            <label
              style={{ display: "flex", alignItems: "center", gap: "10px" }}
            >
              <span style={{ fontWeight: "bold", minWidth: "120px" }}>
                Scholarship Percentage:
              </span>
              <select
                value={scholarshipPercentage}
                onChange={handleScholarshipPercentageChange}
                style={{
                  padding: "8px",
                  borderRadius: "4px",
                  border: "1px solid #ced4da",
                  width: "100px",
                  cursor: "pointer",
                }}
              >
                <option value="10">10%</option>
                <option value="20">20%</option>
                <option value="30">30%</option>
                <option value="40">40%</option>
                <option value="50">50%</option>
              </select>
            </label>
          </div>
        )}

        <div style={{ marginBottom: "15px" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <input
              type="checkbox"
              checked={hasTSI}
              onChange={handleTSIChange}
              id="tsi-checkbox"
              style={{
                cursor: "pointer",
                width: "16px",
                height: "16px",
              }}
            />
            <span
              style={{
                fontWeight: "bold",
                marginLeft: "10px",
                userSelect: "none", // Prevents text selection
              }}
            >
              Has TSI Courses
            </span>
            <button
              onClick={showTSIAlert}
              style={{
                fontSize: "18px",
                backgroundColor: "white",
                color: "blue",
                border: "none",
                borderRadius: "100%",
                cursor: "pointer",
                fontWeight: "bold", // added to make the "?" bold
              }}
            >
              ?
            </button>
          </div>
        </div>

        {hasTSI && (
          <div style={{ marginBottom: "15px" }}>
            <label
              style={{ display: "flex", alignItems: "center", gap: "10px" }}
            >
              <span style={{ fontWeight: "bold", minWidth: "120px" }}>
                Number of TSI Courses:
              </span>
              <select
                value={tsiCourses}
                onChange={handleTSICoursesChange}
                style={{
                  padding: "8px",
                  borderRadius: "4px",
                  border: "1px solid #ced4da",
                  width: "100px",
                  cursor: "pointer",
                }}
              >
                <option value="1">1 Course</option>
                <option value="2">2 Courses</option>
              </select>
              <span
                style={{ fontSize: "0.9em", color: "#666", marginLeft: "10px" }}
              >
                (${config?.tsi_course_cost || 0} each)
              </span>
            </label>
          </div>
        )}
      </div>

      {/* Results Panel */}
      {result && (
        <div
          style={{
            marginTop: "20px",
            padding: "20px",
            backgroundColor: TTU_COLORS.white,
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            border: `1px solid ${TTU_COLORS.red}`,
          }}
        >
          <h2
            style={{
              color: TTU_COLORS.red,
              marginBottom: "15px",
              marginTop: 0,
              fontFamily: "'Roboto', sans-serif",
              borderBottom: `2px solid ${TTU_COLORS.red}`,
              paddingBottom: "8px",
            }}
          >
            Calculation Results
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "10px",
            }}
          >
            {/* Basic Information */}
            <p style={{ margin: "5px 0" }}>
              <strong>Number of Credits:</strong> {result.credits}
            </p>
            <p style={{ margin: "5px 0" }}>
              <strong>Credit Price:</strong> ${config.credit_price.toFixed(2)}
            </p>

            {/* Additional Costs */}
            {result.tsi_cost > 0 && (
              <p style={{ margin: "5px 0" }}>
                <strong>TSI Courses Cost:</strong> ${result.tsi_cost.toFixed(2)}
              </p>
            )}
            <p style={{ margin: "5px 0" }}>
              <strong>Student Services:</strong> $
              {result.student_services.toFixed(2)}
            </p>
            {/* Main Costs */}
            <p style={{ margin: "5px 0" }}>
              <strong>Tuition:</strong> ${result.tuition.toFixed(2)}
            </p>
            <p style={{ margin: "5px 0" }}>
              <strong>Insurance:</strong> ${result.insurance.toFixed(2)}
            </p>
          </div>

          <hr
            style={{
              margin: "20px 0",
              border: "none",
              borderTop: `1px solid ${TTU_COLORS.red}`,
            }}
          />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "10px",
            }}
          >
            {/* Subtotal */}
            <p style={{ margin: "5px 0", paddingTop: "5px" }}>
              <strong>Subtotal (Before Tax):</strong> $
              {result.subtotal.toFixed(2)}
            </p>

            {/* Discounts */}
            {hasScholarship && (
              <p style={{ margin: "5px 0", color: TTU_COLORS.red }}>
                <strong>Scholarship Discount:</strong> -$
                {result.scholarship_amount.toFixed(2)}
              </p>
            )}
            <p style={{ margin: "5px 0" }}>
              <strong>Educational Tax:</strong>{" "}
              {(config.tax_edu * 100).toFixed(1)}%
            </p>
            <p style={{ margin: "5px 0" }}>
              <strong>Services Tax:</strong> {(config.tax_iva * 100).toFixed(1)}
              %
            </p>
          </div>

          {/* Total Cost */}
          <div
            style={{
              marginTop: "20px",
              padding: "15px",
              backgroundColor: TTU_COLORS.lightGray,
              borderRadius: "4px",
              textAlign: "center",
              borderTop: `2px solid ${TTU_COLORS.red}`,
            }}
          >
            <p
              style={{
                fontSize: "1.2em",
                fontWeight: "bold",
                color: TTU_COLORS.red,
                margin: 0,
              }}
            >
              Total Cost: ${result.total_cost.toFixed(2)}
            </p>
            <p style={{ margin: "5px 0" }}>
              <i>
                *Semester cost is just an estimation, prices might vary
                depending on different factors.
              </i>
            </p>
          </div>

          {/* Installment Plan (4 months) */}
          <div style={{ textAlign: "center" }}>
            <p style={{ margin: "5px 0" }}>
              <strong>Installment Plan (4 months):</strong> $
              {(result.total_cost / 4).toFixed(2)}
            </p>
            <p style={{ margin: "5px 0" }}>
              <i>
                *Installment plans of 3 or 6 months can be obtained with BAC or
                Promerica Banks
              </i>
            </p>
          </div>

          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <button
              onClick={handleDownload}
              style={{
                padding: "10px 20px",
                backgroundColor: TTU_COLORS.red,
                color: TTU_COLORS.white,
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                transition: "background-color 0.2s",
                fontSize: "1em",
                fontWeight: "bold",
              }}
              onMouseOver={(e) =>
                (e.target.style.backgroundColor = TTU_COLORS.darkRed)
              }
              onMouseOut={(e) =>
                (e.target.style.backgroundColor = TTU_COLORS.red)
              }
            >
              Download Excel
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          marginTop: "40px",
          textAlign: "center",
          color: TTU_COLORS.black,
          borderTop: `2px solid ${TTU_COLORS.red}`,
          paddingTop: "20px",
        }}
      >
        <p style={{ margin: 0, fontSize: "0.9em" }}>
          Texas Tech University Costa Rica | Semester Cost Calculator
        </p>
      </div>
    </div>
  );
};

export default Calculator;
